import React, { useEffect, useRef, useState } from 'react';
import { MARKET_FALLBACK_IMAGE } from '@/constants/brand';
import { normalizeProductImageSrc } from '@/lib/image-utils';
import { cn } from '@/lib/utils';

/**
 * Ürün görseli: yükleme hatasında veya boş URL'de ana site logosunu gösterir;
 * ürün <img>'i DOM'dan kaldırılır ki kırık ikon çıkmasın.
 */
export function ProductThumbnail({
  src,
  alt = '',
  className,
  /** Yalnızca ürün görseline uygulanır (ör. kart hover ölçeği) */
  coverImgClassName,
  fallbackImgClassName,
  /** Ürün geldiğinde object-cover (kartlar için true) */
  coverWhenReady = true,
  loading = 'lazy',
  /** İç giyim / çıplak görselleri kartta bulanık gösterir */
  sensitive = false,
  /** Görsel yoksa veya açılmazsa (anasayfa kartı listeden düşsün) */
  onUnavailable,
}) {
  const normalized = normalizeProductImageSrc(typeof src === 'string' ? src : '');
  const [failed, setFailed] = useState(() => !normalized);
  const reportedRef = useRef(false);

  const reportUnavailable = () => {
    if (!onUnavailable || reportedRef.current) return;
    reportedRef.current = true;
    onUnavailable();
  };

  useEffect(() => {
    reportedRef.current = false;
    setFailed(!normalized);
    if (!normalized) reportUnavailable();
  }, [normalized]);

  return (
    <div className={cn('relative overflow-hidden bg-muted/50', className)}>
      {normalized && !failed ? (
        <img
          key={normalized}
          src={normalized}
          alt={alt}
          loading={loading}
          decoding="async"
          onError={() => {
            setFailed(true);
            reportUnavailable();
          }}
          className={cn(
            'w-full h-full',
            coverWhenReady ? 'object-cover' : 'object-contain',
            sensitive && 'scale-125 blur-2xl',
            coverImgClassName,
          )}
        />
      ) : null}
      {sensitive && normalized && !failed ? (
        <div className="absolute inset-0 bg-neutral-900/35" aria-hidden />
      ) : null}
      {(failed || !normalized) && (
        <img
          src={MARKET_FALLBACK_IMAGE}
          alt=""
          role="presentation"
          loading={loading}
          decoding="async"
          className={cn('w-full h-full object-contain p-3 sm:p-5 opacity-[0.96]', fallbackImgClassName)}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}
    </div>
  );
}

export default ProductThumbnail;
